import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import Modal from './Modal.tsx';
import { Drag, Pencil, Link as LinkIcon, Globe, Lock, Trash, Plus, Folder, X } from '../lib/icons.tsx';
import type { CategoryNode, CategoryTreeNode } from '../lib/categoryTree.ts';
import { categoryColours, type CategoryColour } from '../../shared/api.ts';
import { swatchVar } from '../../shared/palette.ts';

/** Which level a row belongs to: 'parent' for a top-level row, else its
 *  parent's path. Reordering is constrained to one group. */
type DragGroup = string;
type DragSource = { group: DragGroup; path: string };

/**
 * Manage the organisation categories as a whole — the bulk editor behind the
 * sidebar's "Manage categories" button. Styled like the deck editor's Overview
 * window (shared `.modal` shell + a bespoke bar/scroll). Supports: create an
 * empty category, rename (cascades to decks + children — typing a new parent
 * re-parents a sub-category), drag-to-reorder within a level, delete (decks
 * fall back to Uncategorised), and copy each category's filter/public link.
 *
 * Presentational-ish: the Dashboard owns the data + persistence; this component
 * keeps a local copy of the tree for optimistic drag and calls back on every
 * mutation. `tree` is [{ path, label, count, children:[{path,label,count}] }].
 *
 * `onSetColour` is optional, and the two shelves that store a colour per
 * category pass it: the decks dashboard (categories.json, Run 0) and the
 * Library (library-meta.json's `categoryOrder`, Run 11). The Bank's registry
 * has no colours; omit the prop there and the swatch button isn't rendered at
 * all, which is what keeps that screen untouched by this.
 *
 * `lockedPaths` marks rows that exist but can't be edited — the Bank's four
 * built-in categories (people/logos/brand/facts), whose exact names the server
 * keys behaviour off. They render greyed, undraggable, with rename and delete
 * disabled; their sub-categories stay fully editable.
 */
type ManageCategoriesModalProps = {
  tree: CategoryTreeNode[];
  busy?: boolean;
  lockedPaths?: string[];
  onClose: () => void;
  onCreate: (path: string) => void;
  onRename: (path: string, to: string) => void;
  onDelete: (path: string) => void;
  /** The flat path order categories.json stores: each parent, then its
   *  children. */
  onReorder: (paths: string[]) => void;
  /** Tint a category, or clear it with null. Absent = this registry has no
   *  colours, so no swatch is offered. */
  onSetColour?: ((path: string, colour: CategoryColour | null) => void) | null;
  onCopyFilterLink?: ((path: string) => void) | null;
  onCopyPublicLink?: ((path: string) => void) | null;
};

export default function ManageCategoriesModal({
  tree,
  busy = false,
  lockedPaths = [],
  onClose,
  onCreate,
  onRename,
  onDelete,
  onReorder,
  onSetColour,
  onCopyFilterLink,
  onCopyPublicLink,
}: ManageCategoriesModalProps) {
  const [draft, setDraft] = useState('');
  const [local, setLocal] = useState(tree);
  // Path of the row being renamed inline — one at a time.
  const [renaming, setRenaming] = useState<string | null>(null);
  // Path whose swatch popover is open — also one at a time.
  const [colouring, setColouring] = useState<string | null>(null);
  const drag = useRef<DragSource | null>(null);
  const createRef = useRef<HTMLInputElement>(null);
  // Mirrors `renaming` so the mount-once key handler can read it without
  // re-binding on every keystroke.
  const renamingRef = useRef<string | null>(null);
  renamingRef.current = renaming;
  const colouringRef = useRef<string | null>(null);
  colouringRef.current = colouring;
  // Kept in a ref for the same reason: the mount-once handler below has to be
  // able to shut the popover without re-binding whenever it opens.
  const closeColour = useRef(() => setColouring(null));
  const locked = (path: string) => lockedPaths.some((p) => p.toLowerCase() === String(path).toLowerCase());

  // Re-sync the working copy whenever the server tree changes (after a
  // create/rename/delete/reorder round-trip refreshes it).
  useEffect(() => setLocal(tree), [tree]);

  // This dialog owns Escape itself (Modal is told `escape="none"`), because
  // two things here have a prior claim on the key and a blanket close would
  // take the dialog down under both. Focus restoration is NOT here any more —
  // useFocusTrap returns focus to whatever opened this on unmount.
  useEffect(() => {
    createRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // The inline rename input handles its own Escape (it cancels the rename).
      if (renamingRef.current) return;
      // An open swatch popover takes the keypress first: Escape should shut the
      // thing you just opened, not the window behind it.
      if (colouringRef.current) {
        e.stopPropagation();
        closeColour.current();
        return;
      }
      // A confirm raised FROM here (delete a category) sits on top and owns
      // Escape — both listeners are on document, so without this the one
      // keypress would dismiss the confirm and this modal behind it.
      if (document.querySelector('.modal--confirm')) return;
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const total = local.reduce((n, p) => n + 1 + p.children.length, 0);

  // The flat path order categories.json stores: each parent, then its children.
  function flatOrder(nodes: CategoryTreeNode[]): string[] {
    const out: string[] = [];
    for (const p of nodes) {
      out.push(p.path);
      for (const c of p.children) out.push(c.path);
    }
    return out;
  }

  function commitOrder(nodes: CategoryTreeNode[]) {
    setLocal(nodes);
    onReorder(flatOrder(nodes));
  }

  // Drag reorder, constrained to one level: parents among parents, children
  // among their own siblings. `group` is 'parent' for a top-level row, else the
  // parent path for a child row.
  function onDrop(group: DragGroup, targetPath: string) {
    const src = drag.current;
    drag.current = null;
    if (!src || src.group !== group || src.path === targetPath) return;
    if (group === 'parent') {
      const arr = [...local];
      const from = arr.findIndex((p) => p.path === src.path);
      const to = arr.findIndex((p) => p.path === targetPath);
      if (from === -1 || to === -1) return;
      const [moved] = arr.splice(from, 1);
      if (!moved) return;
      arr.splice(to, 0, moved);
      commitOrder(arr);
    } else {
      const arr = local.map((p) => {
        if (p.path !== group) return p;
        const kids = [...p.children];
        const from = kids.findIndex((c) => c.path === src.path);
        const to = kids.findIndex((c) => c.path === targetPath);
        if (from === -1 || to === -1) return p;
        const [moved] = kids.splice(from, 1);
        if (!moved) return p;
        kids.splice(to, 0, moved);
        return { ...p, children: kids };
      });
      commitOrder(arr);
    }
  }

  function commitRename(path: string, next: string) {
    setRenaming(null);
    const to = next.trim();
    if (!to || to === path) return;
    onRename(path, to);
  }

  function createCategory() {
    const path = draft.trim();
    if (!path) return;
    setDraft('');
    onCreate(path);
  }

  const rowProps: SharedRowProps = {
    busy,
    drag,
    locked,
    renaming,
    colouring,
    onStartRename: setRenaming,
    onCommitRename: commitRename,
    onDrop,
    onDelete,
    onSetColour,
    onToggleColour: (path) => setColouring((open) => (open === path ? null : path)),
    onCopyFilterLink,
    onCopyPublicLink,
  };

  return (
    <Modal
      /* No `title`: the bar below is this dialog's heading, its count and its
         close button in one row, so Modal renders no header of its own and
         labels the dialog by name instead. */
      ariaLabel="Manage categories"
      onClose={onClose}
      className="modal--manage-categories"
      escape="none"
    >
      <header className="manage-cats__bar">
        <div className="manage-cats__title">
          <strong>Manage categories</strong>
          <span className="muted small">{total} categories</span>
        </div>
        <button type="button" className="iconbtn" onClick={onClose} aria-label="Close" title="Close">
          <X />
        </button>
      </header>

      <div className="manage-cats__create">
        <input
          ref={createRef}
          type="text"
          className="modal__input"
          value={draft}
          placeholder="New category, e.g. Clients or Clients/Acme"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              createCategory();
            }
          }}
        />
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={createCategory}
          disabled={busy || !draft.trim()}
        >
          <Plus /> Add
        </button>
      </div>

      <div className="manage-cats__scroll">
        {local.length === 0 ? (
          <p className="muted" style={{ padding: '0.8rem 0.2rem' }}>
            No categories yet. Add one above, or use “Move to category…” on a deck.
          </p>
        ) : (
          local.map((parent) => (
            <div className="manage-cats__group" key={parent.path}>
              <Row node={parent} group="parent" isChild={false} {...rowProps} />
              {parent.children.length > 0 && (
                <div className="manage-cats__children">
                  {parent.children.map((child) => (
                    <Row key={child.path} node={child} group={parent.path} isChild {...rowProps} />
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}

/**
 * Inline rename, replacing the `window.prompt` this modal used to open. Edits
 * the full PATH, not the display label — typing a new parent is how you
 * re-parent a sub-category, and that was the prompt's contract too.
 * Enter commits, Escape and blur cancel.
 */
function RenameField({ path, onCommit }: { path: string; onCommit: (path: string, next: string) => void }) {
  const [value, setValue] = useState(path);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  return (
    <input
      ref={ref}
      type="text"
      className="modal__input manage-cats__rename"
      value={value}
      // Deliberately NOT disabled while busy: a background refresh landing
      // mid-edit would disable the field, blur it, and the blur-cancel below
      // would throw away what the user had typed.
      aria-label={`Rename ${path}`}
      onChange={(e) => setValue(e.target.value)}
      // Cancels rather than commits: a blur is usually a click elsewhere, and
      // silently renaming on the way past is the surprising outcome.
      onBlur={() => onCommit(path, path)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onCommit(path, value);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onCommit(path, path);
        }
      }}
    />
  );
}

/** One row's inline actions — shared by parent and child rows. A locked row has
 *  no actions at all, so it gets a single padlock instead: greyed-out controls
 *  still read as "try me", and this row can never be renamed or deleted. */
function RowActions({
  path,
  colour,
  isLocked,
  busy,
  isColouring,
  onStartRename,
  onDelete,
  onSetColour,
  onToggleColour,
  onCopyFilterLink,
  onCopyPublicLink,
}: {
  path: string;
  colour?: CategoryColour | null;
  isLocked: boolean;
  busy?: boolean;
  isColouring: boolean;
  onStartRename: (path: string) => void;
  onDelete: (path: string) => void;
  onSetColour?: ((path: string, colour: CategoryColour | null) => void) | null;
  onToggleColour: (path: string) => void;
  onCopyFilterLink?: ((path: string) => void) | null;
  onCopyPublicLink?: ((path: string) => void) | null;
}) {
  if (isLocked) {
    return (
      <span className="manage-cats__actions">
        {/* A plain span, not a button — nothing to click, so no hover, focus
            or pointer affordance. */}
        <span className="manage-cats__lock" title="Built-in category: cannot be renamed or deleted">
          <Lock />
        </span>
      </span>
    );
  }
  return (
    <span className="manage-cats__actions">
      {onSetColour && (
        <ColourPicker
          path={path}
          colour={colour ?? null}
          open={isColouring}
          busy={busy}
          onToggle={onToggleColour}
          onPick={onSetColour}
        />
      )}
      <button
        type="button"
        className="iconbtn"
        title="Rename…"
        aria-label={`Rename ${path}`}
        disabled={busy}
        onClick={() => onStartRename(path)}
      >
        <Pencil />
      </button>
      {onCopyFilterLink && (
        <button
          type="button"
          className="iconbtn"
          title="Copy filter link"
          aria-label={`Copy filter link for ${path}`}
          onClick={() => onCopyFilterLink(path)}
        >
          <LinkIcon />
        </button>
      )}
      {onCopyPublicLink && (
        <button
          type="button"
          className="iconbtn"
          title="Copy public link"
          aria-label={`Copy public link for ${path}`}
          onClick={() => onCopyPublicLink(path)}
        >
          <Globe />
        </button>
      )}
      <button
        type="button"
        className="iconbtn manage-cats__del"
        title="Delete category"
        aria-label={`Delete ${path}`}
        disabled={busy}
        onClick={() => onDelete(path)}
      >
        <Trash />
      </button>
    </span>
  );
}

/**
 * The per-category swatch. Colour is a REGISTRY property, so this is the only
 * place it is set; the sidebar rail and the deck cards only read it.
 *
 * The hues are named, never spelt: each swatch fills with `var(--swatch-lime)` and
 * friends from base.css, so a palette or theme change lands here for free and
 * the component holds no colour of its own. "No colour" is the first option
 * and the default — a category is not obliged to have one.
 */
function ColourPicker({
  path,
  colour,
  open,
  busy,
  onToggle,
  onPick,
}: {
  path: string;
  colour: CategoryColour | null;
  open: boolean;
  busy?: boolean;
  onToggle: (path: string) => void;
  onPick: (path: string, colour: CategoryColour | null) => void;
}) {
  return (
    <span className="manage-cats__colour">
      <button
        type="button"
        className="iconbtn"
        title={colour ? `Colour: ${colour}` : 'Set a colour'}
        aria-label={`Set the colour for ${path}`}
        aria-expanded={open}
        disabled={busy}
        onClick={() => onToggle(path)}
      >
        <span
          className={'manage-cats__swatch' + (colour ? '' : ' manage-cats__swatch--none')}
          style={colour ? { background: swatchVar(colour) } : undefined}
          aria-hidden="true"
        />
      </button>
      {open && (
        // Closing on blur rather than a document listener: every option inside
        // is a button, so focus leaving the group IS the click landing
        // elsewhere, and there is nothing here to type into.
        <span
          className="manage-cats__swatches"
          role="group"
          aria-label={`Colour for ${path}`}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onToggle(path);
          }}
        >
          <button
            type="button"
            className={'manage-cats__swatchopt manage-cats__swatch--none' + (colour ? '' : ' is-current')}
            title="No colour"
            aria-label="No colour"
            aria-pressed={!colour}
            onClick={() => {
              onPick(path, null);
              onToggle(path);
            }}
          />
          {categoryColours().map((name) => (
            <button
              key={name}
              type="button"
              className={'manage-cats__swatchopt' + (colour === name ? ' is-current' : '')}
              style={{ background: swatchVar(name) }}
              title={name}
              aria-label={name}
              aria-pressed={colour === name}
              onClick={() => {
                onPick(path, name);
                onToggle(path);
              }}
            />
          ))}
        </span>
      )}
    </span>
  );
}

/**
 * Module-scope on purpose: a component defined inside the parent gets a new
 * identity on every render, so React unmounts and remounts its whole subtree —
 * which would throw away the rename input's value and focus mid-keystroke.
 */
/** The verbs and state every row needs, passed down as one bag so the parent
 *  states them once rather than per row. */
type SharedRowProps = {
  busy?: boolean;
  /** Mutable: `Row` writes the drag source on dragstart. */
  drag: MutableRefObject<DragSource | null>;
  locked: (path: string) => boolean;
  renaming: string | null;
  /** Path whose swatch popover is open, if any. */
  colouring: string | null;
  onStartRename: (path: string) => void;
  onCommitRename: (path: string, next: string) => void;
  onDrop: (group: DragGroup, targetPath: string) => void;
  onDelete: (path: string) => void;
  onSetColour?: ((path: string, colour: CategoryColour | null) => void) | null;
  onToggleColour: (path: string) => void;
  onCopyFilterLink?: ((path: string) => void) | null;
  onCopyPublicLink?: ((path: string) => void) | null;
};

function Row({
  node,
  group,
  isChild,
  ...p
}: SharedRowProps & { node: CategoryNode; group: DragGroup; isChild: boolean }) {
  const isLocked = p.locked(node.path);
  return (
    <div
      className={
        'manage-cats__row' + (isChild ? ' manage-cats__row--child' : '') + (isLocked ? ' manage-cats__row--locked' : '')
      }
      draggable={!p.busy && !isLocked}
      onDragStart={(e) => {
        p.drag.current = { group, path: node.path };
        // Firefox only fires drop when drag data is set.
        e.dataTransfer.setData('text/plain', node.path);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        // A locked row is pinned, so it can't be a drop target either — the
        // dragged row would land in a position the server won't honour.
        if (p.drag.current?.group === group && !isLocked) e.preventDefault();
      }}
      onDrop={() => !isLocked && p.onDrop(group, node.path)}
    >
      <span className="manage-cats__grip" title={isLocked ? '' : 'Drag to reorder'} aria-hidden="true">
        {isLocked ? null : <Drag />}
      </span>
      {!isChild && (
        <span className="manage-cats__icon" aria-hidden="true">
          <Folder />
        </span>
      )}
      {p.renaming === node.path ? (
        <RenameField path={node.path} onCommit={p.onCommitRename} />
      ) : (
        <>
          <span className="manage-cats__label">{node.label}</span>
          <span className="manage-cats__count">{node.count}</span>
          <RowActions
            path={node.path}
            colour={node.colour}
            isLocked={isLocked}
            busy={p.busy}
            isColouring={p.colouring === node.path}
            onStartRename={p.onStartRename}
            onDelete={p.onDelete}
            onSetColour={p.onSetColour}
            onToggleColour={p.onToggleColour}
            onCopyFilterLink={p.onCopyFilterLink}
            onCopyPublicLink={p.onCopyPublicLink}
          />
        </>
      )}
    </div>
  );
}
